import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Typography,
  Box,
  Alert,
  Grid,
  Divider,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { formatCurrency, formatDate } from '../../../utils/formatters';
import api from '../../../services/api';

const ModalVisualizarJuros = ({ open, onClose, parcela }) => {
  const [formData, setFormData] = useState({
    valor_juros: 0,
    juros_aplicados_manualmente: false,
    percentual_juros_aplicado: 0,
    observacoes_juros: '',
    valor_original: 0
  });
  const [aportes, setAportes] = useState([]);
  const [loadingAportes, setLoadingAportes] = useState(false);

  useEffect(() => {
    if (parcela) {
      setFormData({
        valor_juros: Number(parcela.valor_juros || 0),
        juros_aplicados_manualmente: parcela.juros_aplicados_manualmente || false,
        percentual_juros_aplicado: Number(parcela.percentual_juros_aplicado || 0),
        observacoes_juros: parcela.observacoes_juros || '',
        valor_original: Number(parcela.valor_original || 0)
      });
      
      // Carregar aportes se o modal estiver aberto
      if (open && parcela.id_emprestimo) {
        loadAportes(parcela.id_emprestimo);
      }
    }
  }, [parcela, open]);

  const loadAportes = async (emprestimoId) => {
    try {
      setLoadingAportes(true);
      const response = await api.get(`/emprestimos/${emprestimoId}/aportes-detalhados`);
      setAportes(response.data);
    } catch (error) {
      console.error('Erro ao carregar aportes:', error);
      setAportes([]);
    } finally {
      setLoadingAportes(false);
    }
  };

  // Função removida - não é mais necessária para visualização

  const calcularValorTotal = () => {
    return formData.valor_original + formData.valor_juros;
  };

  if (!parcela) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
             <DialogTitle>
         Visualizar Juros da Parcela #{parcela.numero_parcela}
       </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Alert severity="info">
            <Typography variant="body2">
              <strong>Valor Original:</strong> {formatCurrency(formData.valor_original)} | 
              <strong> Valor Total:</strong> {formatCurrency(calcularValorTotal())}
            </Typography>
          </Alert>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Valor Original (Principal)"
              value={formData.valor_original}
              InputProps={{ readOnly: true }}
              variant="outlined"
              margin="normal"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Valor dos Juros"
              type="number"
              value={formData.valor_juros}
                             InputProps={{ readOnly: true }}
               variant="outlined"
               margin="normal"
              InputProps={{
                startAdornment: <Typography variant="body2">R$</Typography>
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                                 <Switch
                   checked={formData.juros_aplicados_manualmente}
                   disabled={true}
                 />
              }
              label="Juros aplicados manualmente"
            />
          </Grid>

          {formData.juros_aplicados_manualmente && (
            <>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Percentual de Juros (%)"
                  type="number"
                  value={formData.percentual_juros_aplicado}
                                     InputProps={{ readOnly: true }}
                   variant="outlined"
                   margin="normal"
                  InputProps={{
                    endAdornment: <Typography variant="body2">%</Typography>
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Observações sobre os Juros"
                  value={formData.observacoes_juros}
                                     InputProps={{ readOnly: true }}
                   variant="outlined"
                   margin="normal"
                   multiline
                   rows={3}
                />
              </Grid>
            </>
          )}

          <Grid item xs={12}>
            <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="h6" gutterBottom>
                Resumo da Parcela
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Typography variant="body2" color="textSecondary">
                    Principal
                  </Typography>
                  <Typography variant="h6">
                    {formatCurrency(formData.valor_original)}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2" color="textSecondary">
                    Juros
                  </Typography>
                  <Typography variant="h6" color="warning.main">
                    {formatCurrency(formData.valor_juros)}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2" color="textSecondary">
                    Total
                  </Typography>
                  <Typography variant="h6" color="primary.main">
                    {formatCurrency(calcularValorTotal())}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Grid>

          {/* Seção de Aportes */}
          {aportes.length > 0 && (
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">
                    Aportes que Afetaram Esta Parcela
                  </Typography>
                  <Chip 
                    label={aportes.length} 
                    size="small" 
                    color="primary" 
                    sx={{ ml: 2 }}
                  />
                </AccordionSummary>
                <AccordionDetails>
                  {loadingAportes ? (
                    <Typography>Carregando aportes...</Typography>
                  ) : (
                    <Box>
                      {aportes.map((aporte, index) => (
                        <Box key={aporte.id} sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                          <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={3}>
                              <Typography variant="subtitle2" color="primary">
                                Aporte #{aporte.numero_aporte}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {formatDate(aporte.data_aporte)}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={3}>
                              <Typography variant="body2" color="textSecondary">
                                Valor Original
                              </Typography>
                              <Typography variant="body1" fontWeight="bold">
                                {formatCurrency(aporte.valor_aporte_original)}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={3}>
                              <Typography variant="body2" color="textSecondary">
                                Juros do Aporte
                              </Typography>
                              <Typography variant="body1" color="warning.main">
                                {formatCurrency(aporte.valor_juros_aporte)}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={3}>
                              <Typography variant="body2" color="textSecondary">
                                Impacto por Parcela
                              </Typography>
                              <Typography variant="body1" color="success.main">
                                +{formatCurrency(aporte.valor_por_parcela + aporte.juros_por_parcela)}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                ({formatCurrency(aporte.valor_por_parcela)} + {formatCurrency(aporte.juros_por_parcela)} juros)
                              </Typography>
                            </Grid>
                          </Grid>
                          {aporte.observacao && (
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="caption" color="textSecondary">
                                {aporte.observacao}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      ))}
                      
                      <Alert severity="info" sx={{ mt: 2 }}>
                        <Typography variant="body2">
                          <strong>Como funciona:</strong> Os aportes são distribuídos entre as parcelas pendentes no momento da criação. 
                          Cada aporte adiciona valor principal e juros às parcelas restantes.
                        </Typography>
                      </Alert>
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            </Grid>
          )}
        </Grid>
      </DialogContent>

             <DialogActions>
         <Button onClick={onClose} variant="contained" color="primary">
           Fechar
         </Button>
       </DialogActions>
    </Dialog>
  );
};

export default ModalVisualizarJuros;

